import { BPMNProcess } from "../types";
import { parseString } from "xml2js";
import { logger } from "../../../../../../../constant/winston";

export function calculateWidthBetweenElements(
	element1: Element,
	element2: Element
) {
	const rect1 = element1.getBoundingClientRect();
	const rect2 = element2.getBoundingClientRect();
	const right1 = rect1.right;
	const left2 = rect2.left;
	const widthBetweenElements = Math.abs(left2 - right1);
	return widthBetweenElements;
}

export function getMissingKeys(prev: BPMNProcess, now: BPMNProcess) {
	const keys = Object.keys(now);
	return keys.filter((key) => {
		if (!prev[key]) {
			return key;
		}
		return JSON.stringify(prev[key]) !== JSON.stringify(now[key]);
	});
}

export function addMissingSequenceFlows(processObject: any) {
	function addIncoming(outgoing: string, key: string) {
		if (
			processObject[outgoing] &&
			processObject[outgoing].type !== "sequenceFlow"
		) {
			const newSequenceFlowId = getConnection(outgoing, key);

			processObject[newSequenceFlowId] = {
				type: "sequenceFlow",
				sourceRef: key,
				targetRef: outgoing,
			};

			processObject[key].outgoing = Array.isArray(processObject[key].outgoing)
				? processObject[key].outgoing.map((item: any) =>
						item === outgoing ? getConnection(outgoing, key) : item
				  )
				: getConnection(processObject[key].outgoing, key);

			if (processObject[outgoing] && processObject[outgoing].incoming) {
				const incoming = processObject[outgoing].incoming;
				if (Array.isArray(incoming)) {
					processObject[outgoing].incoming = incoming.map((item) =>
						item === key ? newSequenceFlowId : item
					);
				} else {
					processObject[outgoing].incoming = newSequenceFlowId;
				}
			}
		}
	}

	Object.keys(processObject).forEach((key) => {
		const currentObject = processObject[key];

		if (currentObject.outgoing) {
			let outgoing = currentObject.outgoing;

			if (!Array.isArray(outgoing)) {
				outgoing = [outgoing];
			}

			outgoing.forEach((item: any) => addIncoming(item, key));
		}
		if (currentObject.type === "subProcess") {
			addMissingSequenceFlows(currentObject.process);
		}
	});

	return processObject;
}

function getConnection(outgoing: string, key: string) {
	return outgoing + "_flow" + "_" + key;
}

function getInOutFromSeqFlow(
	id: string[] | string,
	direction: "in" | "out",
	process: any[]
) {
	const refs = process
		.filter(({ $ }) => $.type === "sequenceFlow" && (Array.isArray(id) ? id.includes($.id) : id === $.id))
		.map(({ $ }) => (direction === "in" ? $.sourceRef : $.targetRef));

	return refs.length === 1 ? refs[0] : refs
}

export function getJSONFromXML(xml: string) {
	const json: Record<string, any> = {};

	let bpmnProcess: any[] = [];

	parseString(xml, (err, data) => {
		if (err) {
			logger.error("error: parseString: failed to parse xml string:", err)
			return bpmnProcess
		}
		const proc = data["bpmn:definitions"]["bpmn:process"];
		if (proc && proc.length) {
			bpmnProcess = Object.entries(proc[0])
				.map(([key, val]: any) => {
					if (!Array.isArray(val)) {
						return false;
					}
					return val.map((item: any) => {
						if(item['$'] && item['$']['camunda:topic'] === 'aiAgentTask') {
							delete item['$']['camunda:topic']
							delete item['$']['camunda:type']
							item['$']['externalTask'] = 'aiAgent'
						}
						return { ...item, $: { ...item["$"], type: key.split(":")[1] } };
					});
				})
				.flat()
				.filter((el) => el);
		}
	});

	bpmnProcess.forEach((item) => {
		const {
			$: info,
			"bpmn:incoming": incoming,
			"bpmn:outgoing": outgoing,
		} = item;

		if (info && info.type !== "sequenceFlow") {
			const { id, ...rest } = info;
			json[id] = rest;

			if (outgoing) {
				json[id] = {
					...json[id],
					outgoing: getInOutFromSeqFlow(outgoing, "out", bpmnProcess),
				};
			}

			if (incoming) {
				json[id] = {
					...json[id],
					incoming: getInOutFromSeqFlow(incoming, "in", bpmnProcess),
				};
			}
		}
	});

	return json
}


export function processExternalTask(processObject: BPMNProcess) {
  function processProcess(process: BPMNProcess) {
    const keyMap: { [oldKey: string]: string } = {};

    for (const [key, element] of Object.entries(process)) {
      if (element.type === "serviceTask" && element.externalTask && !key.endsWith(`_` + element.externalTask)) {
        const newKey = `${key}_${element.externalTask}`;
        keyMap[key] = newKey;
      }
    }

    for (const [oldKey, newKey] of Object.entries(keyMap)) {
      process[newKey] = process[oldKey];
      delete process[oldKey];

      for (const element of Object.values(process)) {
        if ("incoming" in element && element.incoming === oldKey) {
          element.incoming = newKey;
        }
        if ("outgoing" in element && Array.isArray(element.outgoing)) {
          element.outgoing = element.outgoing.map(out => out === oldKey ? newKey : out);
        } else if ("outgoing" in element && element.outgoing === oldKey) {
          element.outgoing = newKey;
        }
      }
    }

    for (const element of Object.values(process)) {
      if (element.type === "subProcess") {
        processProcess(element.process);
      }
    }
  }

  processProcess(processObject);
}
